import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import { Text } from '@mantine/core';
import {
  useSendForgotPasswordEmailMutation,
  useResetPasswordMutation,
} from 'redux/Api/authApi/authApi';
import {
  IconEyeOpen,
  IconEyeClosed,
  IconCheck as RequirementIconCheck,
  IconX as RequirementIconX,
} from 'pages/Profile/icons';
import s from '../Login/Login.module.scss';
import changePasswordStyles from '../Profile/ChangePassword.module.scss';
import logo from '../Login/img/logo.svg';
import { ReactComponent as IconCheck } from '../Registration/img/iconDone.svg';
import { ReactComponent as IconBackForward } from './img/iconBackForward.svg';
import { generatePassword } from 'utils/strActions';

interface EmailFormData {
  email: string;
}

interface PasswordFormData {
  password: string;
  confirmPassword: string;
}

type Step = 'email' | 'confirmation' | 'reset';

export const ForgotPassword: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(true);
  const [showConfirmPassword, setShowConfirmPassword] = useState(true);
  const [currentStep, setCurrentStep] = useState<Step>('email');
  const [submittedEmail, setSubmittedEmail] = useState('');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');
  const emailFromUrl = searchParams.get('email');

  const [sendForgotPasswordEmail] = useSendForgotPasswordEmailMutation();
  const [resetPassword] = useResetPasswordMutation();

  const emailForm = useForm<EmailFormData>({
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
  });

  const passwordForm = useForm<PasswordFormData>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const password = passwordForm.watch('password');
  const confirmPassword = passwordForm.watch('confirmPassword');

  const hasUpperCase = Boolean(/[A-Z]/.test(password));
  const hasLowerCase = Boolean(/[a-z]/.test(password));
  const hasSpecialChar = Boolean(/[!@#$%^&*(),.?":{}|<>]/.test(password));
  const hasNumber = Boolean(/\d/.test(password));
  const hasMinLength = password.length >= 8;
  const hasMaxLength = password.length <= 20;
  const passwordsMatch = Boolean(
    password && confirmPassword && password === confirmPassword,
  );

  useEffect(() => {
    if (token && emailFromUrl) {
      const decodedEmail = decodeURIComponent(emailFromUrl);
      setSubmittedEmail(decodedEmail);
      setCurrentStep('reset');

      const generatedPassword = generatePassword();
      passwordForm.setValue('password', generatedPassword, { shouldValidate: true });
      passwordForm.setValue('confirmPassword', generatedPassword, {
        shouldValidate: true,
      });
    }
  }, [token, emailFromUrl, passwordForm]);

  const onEmailSubmit = async (data: EmailFormData) => {
    setEmailError('');
    setLoading(true);

    try {
      await sendForgotPasswordEmail({ email: data.email }).unwrap();
      setSubmittedEmail(data.email);

      setCurrentStep('confirmation');
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        'Ошибка при отправке кода подтверждения на email';
      setEmailError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (value: string) => {
    if (value.length < 8) {
      return 'Пароль должен содержать минимум 8 символов';
    }
    if (value.length > 20) {
      return 'Пароль должен содержать максимум 20 символов';
    }
    if (!/(?=.*[a-z])/.test(value)) {
      return 'Пароль должен содержать хотя бы одну строчную букву';
    }
    if (!/(?=.*[A-Z])/.test(value)) {
      return 'Пароль должен содержать хотя бы одну заглавную букву';
    }
    if (!/(?=.*\d)/.test(value)) {
      return 'Пароль должен содержать хотя бы одну цифру';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
      return 'Пароль должен содержать хотя бы один спецсимвол';
    }
    return true;
  };

  const onPasswordSubmit = async (data: PasswordFormData) => {
    if (!emailFromUrl || !token) {
      setPasswordError('Недействительная ссылка для восстановления пароля');
      return;
    }

    setPasswordError('');
    setLoading(true);

    try {
      await resetPassword({
        token: token,
        email: emailFromUrl,
        password: data.password,
        password_confirmation: data.confirmPassword,
      }).unwrap();

      navigate('/login', {
        state: {
          passwordResetSuccess: true,
          email: emailFromUrl,
        },
      });
    } catch (error: any) {
      const errorMessage =
        error?.data?.message || error?.message || 'Ошибка при сбросе пароля';
      setPasswordError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentStep === 'reset' && (!token || !emailFromUrl)) {
      setPasswordError(
        'Недействительная ссылка для восстановления пароля. Запросите новую ссылку.',
      );
    }
  }, [currentStep, token, emailFromUrl]);

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: 'password' | 'confirmPassword',
  ) => {
    const value = e.target.value;
    const sanitizedValue = value.replace(/[а-яА-ЯёЁ\s]/g, '');
    passwordForm.setValue(fieldName, sanitizedValue, { shouldValidate: true });
    passwordForm.trigger(fieldName);
    if (fieldName === 'password') {
      const confirm = passwordForm.getValues('confirmPassword');
      if (confirm) {
        passwordForm.trigger('confirmPassword');
      }
    }
  };

  if (currentStep === 'email') {
    return (
      <div className={s.enter}>
        <div className={s.inner}>
          <div className={s.logo}>
            <img src={logo} alt='Скилла' />
          </div>

          <h1 className={s.titleStep}>Восстановить пароль</h1>

          <form className={s.form} onSubmit={emailForm.handleSubmit(onEmailSubmit)}>
            <div className={s.inputGroup}>
              <label>Введи Email</label>
              <input
                type='email'
                required
                placeholder='@mail.ru'
                autoComplete='email'
                className={classNames(
                  s.input,
                  (emailForm.formState.errors?.email?.message || emailError) &&
                    s.input_error,
                )}
                {...emailForm.register('email', {
                  required: 'Поле обязательно для заполнения',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Неверный Email адрес',
                  },
                  onChange: () => {
                    if (emailError) {
                      setEmailError('');
                    }
                  },
                })}
              />
              {emailForm.formState.errors?.email?.message && (
                <div className={s.errorMessage}>
                  {emailForm.formState.errors.email.message}
                </div>
              )}
              {emailError && <div className={s.errorMessage}>{emailError}</div>}
            </div>

            <div className={s.buttonGroup}>
              <button
                type='button'
                className={s.backIconButton}
                onClick={() => navigate('/login')}>
                <IconBackForward />
              </button>

              <button type='submit' disabled={loading} className={s.submitButtonInGroup}>
                Восстановить пароль
              </button>
            </div>
          </form>

          <div className={s.footer}>
            <span>© {new Date().getFullYear()} go.skilla.ru</span>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'confirmation') {
    return (
      <div className={s.enter}>
        <div className={s.inner}>
          <div className={s.logo}>
            <img src={logo} alt='Скилла' />
          </div>

          <h1 className={s.titleStep}>Проверь почту</h1>

          <div className={s.form}>
            <div className={s.inputGroup}>
              <p className={s.description}>
                На почту{' '}
                <a
                  href={`mailto:${submittedEmail}`}
                  className={s.link}
                  style={{ marginRight: '0' }}>
                  {submittedEmail}
                </a>{' '}
                отправлена ссылка для восстановления пароля
              </p>
            </div>

            <button
              type='button'
              className={s.submitButton}
              onClick={() => navigate('/login')}>
              В начало
            </button>
          </div>

          <div className={s.footer}>
            <span>© {new Date().getFullYear()} go.skilla.ru</span>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'reset') {
    return (
      <div className={s.enter}>
        <div className={s.inner}>
          <div className={s.logo}>
            <img src={logo} alt='Скилла' />
          </div>

          <h1 className={s.titleStepRegister}>Восстановление пароля</h1>

          <div className={s.stepperText}>
            <div className={s.stepperTextItem}>
              <IconCheck />
              <span>Пароль обеспечит защиту твоих средств</span>
            </div>
            <div className={s.stepperTextItem}>
              <IconCheck />
              <span>Восстановить пароль можно по Email</span>
            </div>
            <div className={s.stepperTextItem}>
              <IconCheck />
              <span>Мы сгенерировали надежный пароль</span>
            </div>
          </div>

          <form className={s.form} onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}>
            <div className={s.inputGroup}>
              <label>Задай пароль для входа</label>
              <div className={s.passwordWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  placeholder='******'
                  autoComplete='new-password'
                  className={classNames(
                    s.input,
                    passwordForm.formState.errors?.password?.message && s.input_error,
                  )}
                  {...passwordForm.register('password', {
                    required: 'Поле обязательно для заполнения',
                    validate: validatePassword,
                    onChange: (e) => {
                      handlePasswordChange(e, 'password');
                      if (passwordError) {
                        setPasswordError('');
                      }
                      const confirm = passwordForm.getValues('confirmPassword');
                      if (confirm) {
                        passwordForm.trigger('confirmPassword');
                      }
                    },
                  })}
                />
                <button
                  type='button'
                  className={s.eyeButton}
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}>
                  {showPassword ? (
                    <IconEyeClosed size={16} color='#ADBFDF' />
                  ) : (
                    <IconEyeOpen size={16} color='#ADBFDF' />
                  )}
                </button>
              </div>
              {passwordForm.formState.errors?.password?.message && (
                <div className={s.errorMessage}>
                  {passwordForm.formState.errors.password.message}
                </div>
              )}
            </div>

            <div className={s.inputGroup}>
              <label>Повтори пароль</label>
              <div className={s.passwordWrapper}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  placeholder='******'
                  autoComplete='new-password'
                  className={classNames(
                    s.input,
                    passwordForm.formState.errors?.confirmPassword?.message &&
                      s.input_error,
                  )}
                  {...passwordForm.register('confirmPassword', {
                    required: 'Поле обязательно для заполнения',
                    validate: (value) =>
                      value === passwordForm.getValues('password') ||
                      'Пароли не совпадают',
                    onChange: (e) => {
                      handlePasswordChange(e, 'confirmPassword');
                      if (passwordError) {
                        setPasswordError('');
                      }
                    },
                  })}
                />
                <button
                  type='button'
                  className={s.eyeButton}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}>
                  {showConfirmPassword ? (
                    <IconEyeClosed size={16} color='#ADBFDF' />
                  ) : (
                    <IconEyeOpen size={16} color='#ADBFDF' />
                  )}
                </button>
              </div>
              {passwordForm.formState.errors?.confirmPassword?.message && (
                <div className={s.errorMessage}>
                  {passwordForm.formState.errors.confirmPassword.message}
                </div>
              )}
            </div>

            {passwordError && <div className={s.errorMessage}>{passwordError}</div>}

            <button
              type='submit'
              disabled={loading || !passwordForm.formState.isValid}
              className={s.submitButton}>
              Сохранить
            </button>

            <div className={changePasswordStyles.requirements}>
              <div className={changePasswordStyles.requirement}>
                {hasMinLength && hasMaxLength ? (
                  <RequirementIconCheck size={16} color='green' />
                ) : (
                  <RequirementIconX size={16} color='red' />
                )}
                <Text size='sm' ml={8}>
                  От 8 до 20 символов
                </Text>
              </div>
              <div className={changePasswordStyles.requirement}>
                {hasUpperCase && hasLowerCase ? (
                  <RequirementIconCheck size={16} color='green' />
                ) : (
                  <RequirementIconX size={16} color='red' />
                )}
                <Text size='sm' ml={8}>
                  Строчная и прописная буквы латиницей
                </Text>
              </div>
              <div className={changePasswordStyles.requirement}>
                {hasNumber ? (
                  <RequirementIconCheck size={16} color='green' />
                ) : (
                  <RequirementIconX size={16} color='red' />
                )}
                <Text size='sm' ml={8}>
                  Хотя бы одна цифра
                </Text>
              </div>
              <div className={changePasswordStyles.requirement}>
                {hasSpecialChar ? (
                  <RequirementIconCheck size={16} color='green' />
                ) : (
                  <RequirementIconX size={16} color='red' />
                )}
                <Text size='sm' ml={8}>
                  Один спецсимвол (!, @, #)
                </Text>
              </div>
              <div className={changePasswordStyles.requirement}>
                {passwordsMatch ? (
                  <RequirementIconCheck size={16} color='green' />
                ) : (
                  <RequirementIconX size={16} color='red' />
                )}
                <Text size='sm' ml={8}>
                  Пароли совпадают
                </Text>
              </div>
            </div>
          </form>

          <div className={s.footer}>
            <span>© {new Date().getFullYear()} go.skilla.ru</span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
